import type { Duration } from 'date-fns'
import {
  add,
  sub,
  differenceInYears,
  differenceInMonths,
  differenceInWeeks,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInSeconds,
} from 'date-fns'

// Keys of Duration
export enum Interval {
  Years = 'years',
  Months = 'months',
  Weeks = 'weeks',
  Days = 'days',
  Hours = 'hours',
  Minutes = 'minutes',
  Seconds = 'seconds',
}

const differenceIn = {
  [Interval.Years]: differenceInYears,
  [Interval.Months]: differenceInMonths,
  [Interval.Weeks]: differenceInWeeks,
  [Interval.Days]: differenceInDays,
  [Interval.Hours]: differenceInHours,
  [Interval.Minutes]: differenceInMinutes,
  [Interval.Seconds]: differenceInSeconds,
}

export type DurationOptions = Partial<{
  max: Interval
  min: Interval
  maxIntervals: number
}>

/**
 * Processes a date-fns Duration into a new Duration with a desired set of intervals, based on the options given
 */
export function splitDuration(value: Duration, options: DurationOptions = {}): Duration {
  const { min, max, maxIntervals } = options

  const intervals = Object.values(Interval)

  // Set defaults to 'all intervals'
  const maxIntervalIndex = max ? intervals.indexOf(max) : 0
  const minPrecisionIndex = min ? intervals.indexOf(min) : intervals.length
  const maxIntervalCount = maxIntervals || intervals.length

  // Create a new empty/zero duration
  const newDuration: Duration = {}
  let intervalCount = 0

  // Keep track of duration remaining to be processe. Currently, date-fns has limited support for durations (they're
  // working on it) so it's easier to work in dates, where we have access to the full library of utils.
  const start = new Date(0)
  let dateRemaining = add(start, value)

  intervals.forEach((interval, i) => {
    newDuration[interval] = 0 // Start by zero-ing each interval, in case any keys were unset

    // If this interval is allowed to be calculated
    if (i >= maxIntervalIndex && i <= minPrecisionIndex && intervalCount < maxIntervalCount) {
      const intervalValue = differenceIn[interval](dateRemaining, start) // decimals truncated for us

      // If there's duration at this interval to be processed
      if (intervalValue > 0) {
        intervalCount += 1
        newDuration[interval] = intervalValue
        // Remove that amount of time from the remaining duration, so that it isn't counted twice
        const processedDuration = { [interval]: intervalValue }
        dateRemaining = sub(dateRemaining, processedDuration)
      }
    }
  })

  return newDuration
}
